import {HttpClient, HttpContext, HttpHeaders} from '@angular/common/http'
import {inject, Service} from '@angular/core'
import {Interest} from '@sparbanken-syd/rt-backend'
import {X_API_KEY} from '@sparbanken-syd/sparbanken-syd-bankid'
import {BehaviorSubject, Observable, tap} from 'rxjs'
import {filter, map, switchMap} from 'rxjs/operators'
import {environment} from '../../environments/environment'
import {SECRET} from '../../environments/secret'
import {InterestType, UsageType} from '../application/data-types'
import {LatestInterest, RtJson} from './types'

@Service()
export class InterestService {
  /**
   * Consumers should listen to this to get all that regards interests.
   */
  public interests$: BehaviorSubject<Interest[]> = new BehaviorSubject<Interest[]>([])
  public latestInterest$ = new BehaviorSubject<LatestInterest>({} as any)
  public interestTypes$: BehaviorSubject<InterestType[]> = new BehaviorSubject<InterestType[]>([])
  private readonly httpClient = inject(HttpClient)

  /**
   * Fetches all interests and updates the internal stream.
   */
  public getAllInterests(): Observable<Interest[]> {
    const url = `${environment.apiUrl}/interests`
    return this.httpClient.get<Interest[]>(url).pipe(
      tap(allInterests => this.interests$.next(allInterests))
    )
  }

  /**
   * Get the latest valid interest from the database.
   */
  public getLatestInterest(typeOfInterest: string) {
    const context = new HttpContext().set(X_API_KEY, SECRET)
    const url = `${environment.apiUrl}/interests/latest/${typeOfInterest}`
    return this.httpClient.get<LatestInterest|null>(url, {context})
      .pipe(
        filter(Boolean),
        map(interest => {
          this.latestInterest$.next(interest)
          return interest
        })
      )
  }

  /**
   * Get the new disclaimer text from backend.
   */
  public getNewDisclaimer(type: string): Observable<string[]> {
    const context = new HttpContext().set(X_API_KEY, SECRET)
    const url = `${environment.apiUrl}/interests/disclaimer?typeOfInterest=${type}&ts=${Date.now()}`
    return this.httpClient.get<string[]>(url, {context})
  }

  /**
   * Get disclaimer text from the backend.
   * @deprecated
   */
  public getDisclaimerText(p: number, a: number, iR: number, sF: number, vI: number): Observable<string> {
    const context = new HttpContext().set(X_API_KEY, SECRET)
    const url = `${environment.apiUrl}/interests/disclaimer?periods=${p}&amount=${a}&interestRate=${iR}&startFee=${sF}&interestStartDate=${vI}`

    // Create headers to specify that we accept a plain text response.
    const headers = new HttpHeaders({
      'Accept': 'text/plain; charset=utf-8'
    })

    // Pass the headers along with the request.
    // 'responseType: text' tells Angular to not parse the response as JSON.
    return this.httpClient.get(url, {context, headers, responseType: 'text'})
  }

  /**
   * Get disclaimer json with more info about effective interest from the backend.
   */
  public getDisclaimerJson(p: number, a: number, iR: number, sF: number, vI: number): Observable<RtJson> {
    const context = new HttpContext().set(X_API_KEY, SECRET)
    const url =
      `${environment.apiUrl}/interests/disclaimer?periods=${p}&amount=${a}&interestRate=${iR}&startFee=${sF}&interestStartDate=${vI}`
    return this.httpClient.get<RtJson>(url, {context})
  }

  /**
   * Save/add a new interest to the database and refresh the stream.
   */
  public addInterests(interest: Interest): Observable<Interest[]> {
    const url = `${environment.apiUrl}/interests`
    return this.httpClient.put<Interest>(url, interest)
      .pipe(
        switchMap(() => this.getAllInterests())
      )
  }

  /**
   * Save/update an existing interest in the database and refresh the stream.
   */
  public updateInterests(interest: Interest): Observable<Interest[]> {
    const url = `${environment.apiUrl}/interests/${interest.itemId}`
    return this.httpClient.put<Interest>(url, interest)
      .pipe(
        switchMap(() => this.getAllInterests())
      )
  }

  /**
   * Approve an existing interest in the database and refresh the stream.
   */
  public approveInterests(interest: Interest): Observable<Interest[]> {
    const url = `${environment.apiUrl}/interests/approve`
    return this.httpClient.put<Interest>(url, interest)
      .pipe(
        switchMap(() => this.getAllInterests())
      )
  }

  /**
   * Delete an interest from the database.
   */
  public deleteInterest(id: string): Observable<void> {
    const url = `${environment.apiUrl}/interests/${id}`
    return this.httpClient.delete<void>(url)
  }

  /**
   * Get all interest types from the database.
   */
  public getAllInterestTypes(): Observable<InterestType[]> {
    const url = `${environment.apiUrl}/interest-types`
    return this.httpClient.get<InterestType[]>(url)
      .pipe(
        tap((interestTypes: InterestType[]) => { // Use tap to avoid changing the stream's output
          this.interestTypes$.next(interestTypes)
        })
      )
  }

  /**
   * Save/update an existing interest type in the database and refresh the stream.
   */
  public updateInterestType(interestType: InterestType): Observable<InterestType[]> {
    const url = `${environment.apiUrl}/interest-types/${interestType.itemId}`
    return this.httpClient.put<InterestType>(url, interestType)
      .pipe(
        switchMap(() => this.getAllInterestTypes())
      )
  }

  /**
   * Get information about usage of API KEYS.
   */
  public getKeysInformation(keyId: string, startDate: string, endDate: string): Observable<UsageType> {
    const url = `${environment.apiUrl}/keys?keyId=${keyId}&startDate=${startDate}&endDate=${endDate}`
    return this.httpClient.get<UsageType>(url)
  }
}